/**
 * Situational Awareness Layer — Helper #2: Required Inputs (Phase 2)
 * ──────────────────────────────────────────────────────────────────
 * Companion to `getAvailableActions`. Where that helper answers
 * "What can I do with this entity right now?", this one answers
 * "What data must I capture to do it correctly?"
 *
 * RULE (enforced via mem://architecture/situational-awareness-layer):
 * Before adding any `if (issue.type === ...)` or `if (job.stage === ...)`
 * conditional that gates required form fields, check this file. If the
 * helper doesn't cover it, extend the helper — do not inline.
 *
 * Scenario precedence — single source of truth, enforced by
 * `resolveIssueScenario` below:
 *   1. blockerCategory wins for Material / Access / Permit
 *      (these are operational blockers requiring structured handling
 *       regardless of how the issue was originally typed)
 *   2. issue.type wins for Service
 *      (a service interaction is fundamentally a service flow,
 *       even when a missing part surfaces alongside it)
 *   3. Fallback to "generic" — caller renders the existing free-text path.
 */
import type { Issue, Job } from "../types";

// ────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────

export type IssueScenario = "material" | "access" | "scheduling" | "service" | "permit" | "generic";

/** A single field the resolution form must collect. */
export interface RequiredInputField {
  /** Stable key for form state. */
  key: string;
  /** Human-readable label rendered in the UI. */
  label: string;
  /** Input control hint. UI layer maps this to the actual component. */
  kind: "text" | "textarea" | "select" | "yesno" | "date";
  /** Options for `select` kind. */
  options?: ReadonlyArray<{ value: string; label: string }>;
  /** True when the field must be filled before resolution can be confirmed. */
  required: boolean;
  /** Optional helper text shown beneath the field. */
  helper?: string;
  /** Placeholder for free-text inputs. */
  placeholder?: string;
  /**
   * When set, this field is auto-derived from a runtime signal. The modal
   * will hide it from the manual form and pre-fill the value from the
   * indicated source. Eliminates redundant clicks when the system already
   * knows the answer (e.g., outreach was logged → "Customer contacted: Yes").
   *
   *   "outreach.customer"    — set true if homeowner outreach was logged
   *   "outreach.contractor"  — set true if crew/contractor outreach was logged
   *   "outreach.vendor"      — set true if vendor outreach was logged
   *   "scheduler.bookedDate" — auto-fills with the date of a booked visit
   *                            (eliminates the duplicate "New target date"
   *                            calendar when the operator just booked one).
   */
  derivedFrom?:
    | "outreach.customer"
    | "outreach.contractor"
    | "outreach.vendor"
    | "scheduler.bookedDate";
}

export interface RequiredInputsResult {
  /** Which scenario this resolved to — drives downstream labeling. */
  scenario: IssueScenario;
  /** Ordered list of fields the form should render. */
  fields: ReadonlyArray<RequiredInputField>;
  /** Scenario-specific label for the optional note (never generic "Notes"). */
  noteLabel: string;
  /** Placeholder for the optional note. */
  notePlaceholder: string;
  /** True when the form should still show the optional note alongside structured fields. */
  allowOptionalNote: boolean;
}

/** Normalized resolution payload — feeds the existing activity log path. */
export interface ResolutionPayload {
  scenario: IssueScenario;
  /** Human-readable summary, safe to display in activity log / issue history. */
  summary: string;
  /** Structured field values keyed by field.key. */
  fields: Record<string, string | boolean>;
  /** Optional free-text note (scenario-labeled in the UI). */
  note?: string;
}

// ────────────────────────────────────────────────────────────────────
// Scenario resolver — single source of truth
// ────────────────────────────────────────────────────────────────────

// Site-access signals: physical entry blocked (lockbox, gate, key, on-site).
// Intentionally narrow — generic "customer unavailable" is a SCHEDULING signal,
// not an access signal. Access requires a physical-entry cue.
const ACCESS_DESC_RX = /lockbox|gate code|key|locked out|no.?access|on.?site lock/i;
const SCHEDULING_DESC_RX = /reschedul|conflict|availab|present|coordinat|date|time slot|window|appointment/i;
const SERVICE_DESC_RX = /damage|leak|repair|warranty|defect|callback/i;

/**
 * Deterministic mapping from an Issue to its structured-resolution scenario.
 * Precedence (do not inline this logic anywhere else):
 *   1. Scheduling signals win first — issueType, affectedArea, or "Scheduling Conflict"
 *      blocker. Operators dealing with date/time coordination need scheduling fields,
 *      not access-control fields.
 *   2. Material / Permit blockers — operational blockers requiring structured handling.
 *   3. Access ONLY when there's a real site-entry signal (lockbox/gate/key in description
 *      OR HOA Documentation blocker). "Customer Unavailable" alone routes to scheduling.
 *   4. issue.type → Service wins via Customer Complaint / Install Issue.
 *   5. affectedArea / description regex as last structured signals.
 *   6. Everything else falls back to "generic".
 */
export function resolveIssueScenario(issue: Issue | null | undefined): IssueScenario {
  if (!issue) return "generic";

  const cat = issue.blockerCategory;
  const area = issue.affectedArea;
  const type = issue.issueType;
  const desc = issue.description ?? "";

  // 1. Scheduling signals win first — coordinating dates/crews/customer availability
  //    is fundamentally different from a physical access lockout.
  if (
    type === "Scheduling Issue" ||
    area === "Scheduling" ||
    cat === "Scheduling Conflict" ||
    cat === "Weather Delay" ||
    cat === "Crew Unavailable"
  ) {
    return "scheduling";
  }

  // 2. Material / Permit blockers.
  if (cat === "Missing Material" || cat === "Damaged Material" || cat === "Supplier Delay") {
    return "material";
  }
  if (cat === "Permit Rejection" || cat === "Inspection Failure") {
    return "permit";
  }

  // 3. Access — only when there's a true site-entry signal.
  if (cat === "HOA Documentation") {
    return "access";
  }
  if (cat === "Customer Unavailable") {
    // Disambiguate: physical access cue → access; otherwise scheduling.
    if (ACCESS_DESC_RX.test(desc)) return "access";
    return "scheduling";
  }

  // 4. issue.type wins for Service.
  if (type === "Customer Complaint" || type === "Install Issue") {
    return "service";
  }

  // 5. Structured fall-throughs by type / area / desc.
  if (type === "Material Issue" || type === "Ordering Issue" || area === "Materials") {
    return "material";
  }
  if (type === "Permit Issue" || area === "Permit") {
    return "permit";
  }
  if (ACCESS_DESC_RX.test(desc)) return "access";
  if (SCHEDULING_DESC_RX.test(desc)) return "scheduling";
  if (SERVICE_DESC_RX.test(desc)) return "service";

  // 6. Fallback.
  return "generic";
}

// ────────────────────────────────────────────────────────────────────
// Scenario field definitions
// ────────────────────────────────────────────────────────────────────

const MATERIAL_FIELDS: ReadonlyArray<RequiredInputField> = [
  {
    key: "actionTaken",
    label: "Action taken",
    kind: "select",
    required: true,
    options: [
      { value: "ordered_replacement", label: "Ordered replacement" },
      { value: "reordered_missing", label: "Reordered missing item" },
      { value: "verified_inventory", label: "Verified inventory" },
      { value: "waiting_vendor", label: "Waiting on vendor" },
    ],
  },
  { key: "vendor", label: "Vendor selected", kind: "text", required: false, placeholder: "Vendor name (if applicable)" },
  { key: "eta", label: "Expected replacement ETA", kind: "date", required: false },
  { key: "customerNotified", label: "Customer notified", kind: "yesno", required: true },
];

const ACCESS_FIELDS: ReadonlyArray<RequiredInputField> = [
  {
    key: "accessProblem",
    label: "Access problem type",
    kind: "select",
    required: true,
    options: [
      { value: "locked", label: "Locked / unavailable" },
      { value: "hoa_gate", label: "HOA / gate / building restriction" },
      { value: "customer_unavailable", label: "Customer unavailable" },
      { value: "obstruction", label: "Pet / safety / obstruction" },
    ],
  },
  { key: "customerContacted", label: "Customer contacted", kind: "yesno", required: true, derivedFrom: "outreach.customer" },
  { key: "followUpRequired", label: "Follow-up required", kind: "yesno", required: true },
  { key: "visitRescheduled", label: "Visit rescheduled", kind: "yesno", required: true },
];

// Scheduling — coordinating dates, crews, and customer availability.
// Distinct from access (physical site entry). Fields match a CSR's real
// decision tree when a customer wants to be present, a crew falls through,
// or a date needs to move.
const SCHEDULING_FIELDS: ReadonlyArray<RequiredInputField> = [
  {
    key: "schedulingProblem",
    label: "Scheduling problem",
    kind: "select",
    required: true,
    options: [
      { value: "customer_specific_date", label: "Customer requested specific date / window" },
      { value: "customer_unreachable", label: "Customer unreachable to confirm" },
      { value: "crew_conflict", label: "Crew conflict / unavailable" },
      { value: "weather_delay", label: "Weather delay" },
      { value: "permit_timing", label: "Waiting on permit / inspection timing" },
      { value: "customer_rescheduled", label: "Customer requested reschedule" },
    ],
  },
  { key: "customerContacted", label: "Customer contacted", kind: "yesno", required: true, derivedFrom: "outreach.customer" },
  {
    key: "targetDate",
    label: "New target date",
    kind: "date",
    required: false,
    helper: "Auto-fills when you book a visit above",
    derivedFrom: "scheduler.bookedDate",
  },
  { key: "contractorCoordinated", label: "Contractor / crew coordinated", kind: "yesno", required: true, derivedFrom: "outreach.contractor" },
];

const SERVICE_FIELDS: ReadonlyArray<RequiredInputField> = [
  {
    key: "handledBy",
    label: "Issue handled by",
    kind: "select",
    required: true,
    options: [
      { value: "phone_guidance", label: "Phone guidance" },
      { value: "service_visit", label: "Service visit needed" },
      { value: "subcontractor", label: "Subcontractor / crew follow-up" },
      { value: "parts_required", label: "Parts/materials required" },
    ],
  },
  { key: "homeownerContacted", label: "Homeowner contacted", kind: "yesno", required: true, derivedFrom: "outreach.customer" },
  { key: "scheduleServiceCall", label: "Schedule service call", kind: "yesno", required: true },
];

const PERMIT_FIELDS: ReadonlyArray<RequiredInputField> = [
  {
    key: "permitStatus",
    label: "Permit status",
    kind: "select",
    required: true,
    options: [
      { value: "submitted", label: "Submitted" },
      { value: "pending_municipality", label: "Pending municipality" },
      { value: "rfi", label: "Request for information" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ],
  },
  { key: "authorityContactNeeded", label: "Municipality / authority contact needed", kind: "yesno", required: true },
  { key: "followUpDate", label: "Follow-up due date", kind: "date", required: false },
];

// ────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────

export function getRequiredInputs(
  issue: Issue | null | undefined,
  _job?: Job | undefined,
): RequiredInputsResult {
  const scenario = resolveIssueScenario(issue);

  switch (scenario) {
    case "material":
      return {
        scenario,
        fields: MATERIAL_FIELDS,
        noteLabel: "Vendor / ETA details",
        notePlaceholder: "Anything specific about the vendor, sourcing, or ETA?",
        allowOptionalNote: true,
      };
    case "access":
      return {
        scenario,
        fields: ACCESS_FIELDS,
        noteLabel: "Access details",
        notePlaceholder: "Anything specific about the access situation?",
        allowOptionalNote: true,
      };
    case "scheduling":
      return {
        scenario,
        fields: SCHEDULING_FIELDS,
        noteLabel: "Scheduling details",
        notePlaceholder: "Customer preferences, crew constraints, or coordination notes?",
        allowOptionalNote: true,
      };
    case "service":
      return {
        scenario,
        fields: SERVICE_FIELDS,
        noteLabel: "Repair details",
        notePlaceholder: "Anything specific about the repair or homeowner concern?",
        allowOptionalNote: true,
      };
    case "permit":
      return {
        scenario,
        fields: PERMIT_FIELDS,
        noteLabel: "Permit follow-up note",
        notePlaceholder: "Municipality contact, RFI details, next step?",
        allowOptionalNote: true,
      };
    default:
      return {
        scenario: "generic",
        fields: [],
        noteLabel: "Resolution note",
        notePlaceholder: "What was done to resolve this?",
        allowOptionalNote: true,
      };
  }
}

// ────────────────────────────────────────────────────────────────────
// Output normalization
// ────────────────────────────────────────────────────────────────────

/** Convert a structured field value into a readable fragment. */
function describeFieldValue(field: RequiredInputField, value: string | boolean | undefined): string | null {
  if (value === undefined || value === "" || value === null) return null;
  if (field.kind === "yesno") {
    return `${field.label}: ${value ? "Yes" : "No"}`;
  }
  if (field.kind === "select" && field.options) {
    const opt = field.options.find((o) => o.value === value);
    return opt ? `${field.label}: ${opt.label}` : null;
  }
  if (field.kind === "date" && typeof value === "string") {
    try {
      return `${field.label}: ${new Date(value).toLocaleDateString()}`;
    } catch {
      return `${field.label}: ${value}`;
    }
  }
  return `${field.label}: ${value}`;
}

/**
 * Normalize structured field values + optional note into a payload that the
 * existing issue/activity log can consume without schema change.
 */
export function buildResolutionPayload(
  spec: RequiredInputsResult,
  values: Record<string, string | boolean>,
  note?: string,
): ResolutionPayload {
  const parts: string[] = [];
  for (const f of spec.fields) {
    const piece = describeFieldValue(f, values[f.key]);
    if (piece) parts.push(piece);
  }
  const trimmedNote = note?.trim() ? note.trim() : undefined;
  const summary =
    parts.length > 0
      ? trimmedNote
        ? `${parts.join(" · ")} — ${trimmedNote}`
        : parts.join(" · ")
      : trimmedNote ?? "";

  return {
    scenario: spec.scenario,
    summary,
    fields: { ...values },
    note: trimmedNote,
  };
}

/**
 * Validate that all required fields have non-empty values.
 * Returns missing field labels (empty array = valid).
 */
export function validateRequiredInputs(
  spec: RequiredInputsResult,
  values: Record<string, string | boolean>,
): string[] {
  const missing: string[] = [];
  for (const f of spec.fields) {
    if (!f.required) continue;
    const v = values[f.key];
    if (f.kind === "yesno") {
      if (typeof v !== "boolean") missing.push(f.label);
    } else {
      if (v === undefined || v === null || v === "") missing.push(f.label);
    }
  }
  return missing;
}
